import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dailyBackupAPI } from '../services/api';
import './DailyBackupPage.css';

const ENV_OPTIONS = ['LOCAL', 'AUS', 'US'];

const STATUS_COLORS = {
  RUNNING: '#3182ce',
  COMPLETED: '#48bb78',
  PARTIAL: '#ecc94b',
  FAILED: '#f56565',
  CANCELLED: '#a0aec0',
};

function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
}

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

export default function DailyBackupPage() {
  const [envLabel, setEnvLabel] = useState(() => localStorage.getItem('daily_backup_env') || 'LOCAL');
  const [statusData, setStatusData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCleanup, setShowCleanup] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('daily_backup_env', envLabel);
  }, [envLabel]);

  // Initial + env-change load
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [statusResp, runsResp] = await Promise.all([
          dailyBackupAPI.status(envLabel),
          dailyBackupAPI.listRuns({ env_label: envLabel, page_size: 30 }),
        ]);
        if (cancelled) return;
        setStatusData(statusResp.data);
        setRuns(runsResp.data.runs || []);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [envLabel]);

  // Poll active run if present
  useEffect(() => {
    const activeRunId = statusData?.active_run?.run_id;
    if (!activeRunId) return undefined;
    const interval = setInterval(async () => {
      try {
        const resp = await dailyBackupAPI.status(envLabel);
        setStatusData(resp.data);
        if (!resp.data.active_run) {
          // Run finished — refresh history
          const runsResp = await dailyBackupAPI.listRuns({ env_label: envLabel, page_size: 30 });
          setRuns(runsResp.data.runs || []);
        }
      } catch (_) {
        // ignore transient
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [statusData?.active_run?.run_id, envLabel]);

  // WebSocket for the active run
  useEffect(() => {
    const activeRunId = statusData?.active_run?.run_id;
    if (!activeRunId) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return undefined;
    }
    try {
      const baseUrl = localStorage.getItem('api_base_url') || `http://${window.location.hostname}:8123`;
      const wsUrl = baseUrl.replace(/^http/, 'ws') + `/ws/${activeRunId}`;
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setStatusData((prev) => prev && {
            ...prev,
            active_run: prev.active_run && {
              ...prev.active_run,
              live: message,
            },
          });
        } catch {
          // ignore
        }
      };
      wsRef.current = ws;
      return () => {
        ws.close();
        wsRef.current = null;
      };
    } catch {
      return undefined;
    }
  }, [statusData?.active_run?.run_id]);

  const handleSelectRun = async (runId) => {
    setSelectedRun(runId);
    setRunDetail(null);
    try {
      const resp = await dailyBackupAPI.getRun(runId);
      setRunDetail(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleRunNow = async () => {
    if (!statusData?.enabled) {
      setError('Daily backup feature is dormant — flip DAILY_BACKUP_ENABLED to true first.');
      return;
    }
    if (!window.confirm(`Run daily backup for ${envLabel} right now?`)) return;
    try {
      await dailyBackupAPI.runNow({ envLabel });
      const resp = await dailyBackupAPI.status(envLabel);
      setStatusData(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const liveProgress = useMemo(() => {
    const live = statusData?.active_run?.live;
    if (!live) return null;
    return {
      progress: live.progress || 0,
      operation: live.operation || '',
    };
  }, [statusData?.active_run?.live]);

  return (
    <div className="daily-backup-page container">
      <header className="daily-backup-header">
        <div>
          <h1>Daily Backup</h1>
          <p className="text-muted">
            Automated all-cemetery backup pass. Sequential, off-hours, with retry once and Google Chat notifications.
          </p>
        </div>
        <div className="daily-backup-controls">
          <label htmlFor="env-select">Environment</label>
          <select
            id="env-select"
            value={envLabel}
            onChange={(event) => setEnvLabel(event.target.value)}
          >
            {ENV_OPTIONS.map((env) => (
              <option key={env} value={env}>{env}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={handleRunNow}>
            Run now
          </button>
          <button className="btn btn-sm" onClick={() => setShowCleanup(true)}>
            Cleanup…
          </button>
        </div>
      </header>

      {!statusData?.enabled && (
        <div className="daily-backup-banner daily-backup-banner-warning">
          Schedule disabled (<code>DAILY_BACKUP_ENABLED=false</code>). Set <code>DAILY_BACKUP_ENABLED=true</code>
          and a cron expression in <code>DAILY_BACKUP_CRON</code> to activate scheduled runs. Manual run-now still works.
        </div>
      )}

      {error && (
        <div className="daily-backup-banner daily-backup-banner-error">
          {error}
        </div>
      )}

      {statusData?.active_run && (
        <section className="daily-backup-active card">
          <h2>Run in progress</h2>
          <div className="active-meta">
            <span className="active-pill" style={{ background: STATUS_COLORS.RUNNING }}>RUNNING</span>
            <span>run_id: <code>{statusData.active_run.run_id}</code></span>
            <span>{statusData.active_run.succeeded}/{statusData.active_run.total} done · {statusData.active_run.failed} failed</span>
          </div>
          {liveProgress && (
            <div className="active-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${liveProgress.progress}%` }} />
              </div>
              <div className="progress-label">{liveProgress.progress}% — {liveProgress.operation}</div>
            </div>
          )}
        </section>
      )}

      <section className="daily-backup-runs card">
        <h2>Recent runs</h2>
        {loading && <div className="text-muted">Loading…</div>}
        {!loading && runs.length === 0 && <div className="no-results">No runs recorded yet for {envLabel}.</div>}
        {!loading && runs.length > 0 && (
          <table className="daily-backup-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Status</th>
                <th>Succeeded</th>
                <th>Failed</th>
                <th>Total</th>
                <th>Duration</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.run_id}>
                  <td>{formatDate(run.started_at)}</td>
                  <td>
                    <span className="run-status" style={{ background: STATUS_COLORS[run.status] || '#666' }}>
                      {run.status}
                    </span>
                  </td>
                  <td>{run.succeeded}</td>
                  <td>{run.failed}</td>
                  <td>{run.total}</td>
                  <td>{formatDuration(run.duration_seconds)}</td>
                  <td>
                    <button className="btn btn-sm" onClick={() => handleSelectRun(run.run_id)}>
                      Inspect
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selectedRun && runDetail && (
        <section className="daily-backup-detail card">
          <header>
            <h2>Run {selectedRun.slice(0, 8)}…</h2>
            <button className="btn btn-sm" onClick={() => { setSelectedRun(null); setRunDetail(null); }}>
              Close
            </button>
          </header>
          <div className="detail-meta">
            <span>Status: <strong>{runDetail.status}</strong></span>
            <span>Total: {runDetail.total}</span>
            <span>Succeeded: {runDetail.succeeded}</span>
            <span>Failed: {runDetail.failed}</span>
            <span>Duration: {formatDuration(runDetail.duration_seconds)}</span>
          </div>
          <table className="daily-backup-table">
            <thead>
              <tr>
                <th>Cemetery</th>
                <th>Status</th>
                <th>Retries</th>
                <th>Size</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {(runDetail.cemeteries || []).map((row) => (
                <tr key={row.id}>
                  <td>{row.cemetery_unique_name}</td>
                  <td>{row.status}</td>
                  <td>{row.retry_count}</td>
                  <td>{row.file_size ? `${(row.file_size / 1024 / 1024).toFixed(1)} MB` : '—'}</td>
                  <td>{formatDuration(row.duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {showCleanup && (
        <CleanupModal
          envLabel={envLabel}
          onClose={() => setShowCleanup(false)}
          onError={setError}
        />
      )}
    </div>
  );
}

function CleanupModal({ envLabel, onClose, onError }) {
  const [beforeDate, setBeforeDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  });
  const [keepPerCemetery, setKeepPerCemetery] = useState('');
  const [dryRun, setDryRun] = useState(null);
  const [busy, setBusy] = useState(false);

  const runDryRun = async () => {
    setBusy(true);
    try {
      const resp = await dailyBackupAPI.cleanup({
        beforeDate: new Date(beforeDate).toISOString(),
        envLabel,
        keepPerCemetery: keepPerCemetery ? parseInt(keepPerCemetery, 10) : null,
        confirm: false,
      });
      setDryRun(resp.data);
    } catch (err) {
      onError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!dryRun || dryRun.candidate_count === 0) return;
    if (!window.confirm(`Permanently delete ${dryRun.candidate_count} backups?`)) return;
    setBusy(true);
    try {
      const resp = await dailyBackupAPI.cleanup({
        beforeDate: new Date(beforeDate).toISOString(),
        envLabel,
        keepPerCemetery: keepPerCemetery ? parseInt(keepPerCemetery, 10) : null,
        confirm: true,
      });
      setDryRun(resp.data);
    } catch (err) {
      onError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <h2>Cleanup daily backups</h2>
        <p className="text-muted">
          Removes daily-tagged backup ZIPs older than the cutoff date. Dry-run by default.
        </p>
        <label>
          Before date
          <input type="date" value={beforeDate} onChange={(event) => setBeforeDate(event.target.value)} />
        </label>
        <label>
          Keep N most recent per cemetery (optional)
          <input
            type="number"
            min="0"
            value={keepPerCemetery}
            onChange={(event) => setKeepPerCemetery(event.target.value)}
          />
        </label>
        <div className="modal-actions">
          <button className="btn btn-sm" onClick={runDryRun} disabled={busy}>
            Dry-run
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={confirmDelete}
            disabled={busy || !dryRun || dryRun.candidate_count === 0}
          >
            Delete {dryRun ? dryRun.candidate_count : 0}
          </button>
          <button className="btn btn-sm" onClick={onClose} disabled={busy}>Close</button>
        </div>
        {dryRun && (
          <div className="modal-summary">
            <div>{dryRun.dry_run ? 'Dry-run' : 'Executed'} — {dryRun.candidate_count} candidates · deleted {dryRun.deleted}</div>
            {dryRun.errors && dryRun.errors.length > 0 && (
              <div className="modal-errors">{dryRun.errors.length} error(s) during deletion.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
